<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Games from "@/services/Games";
import Swal from "sweetalert2";

export default {
  components: {
    Layout,
    PageHeader,
  },
  page: {
    title: "Quiz bulk approval",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      csrf_token: localStorage.getItem("csrf_token"),
      approvalLoading: {},
      /** 'approve' | 'reject' — which button shows the spinner */
      approvalAction: {},
      title: "Quiz bulk approval",
      items: [
        { text: "Fanzone", href: "#" },
        { text: "Quiz", href: "/monitor/quiz" },
        { text: "Bulk approval", active: true },
      ],
      minReviewers: 3,
      tableData: [],
      isBusy: false,
      currentPage: 1,
      perPage: 25,
      totalRows: 0,
      sortBy: "id",
      sortDesc: true,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: true,
          tdClass: "align-top quiz-bulk-id-td",
        },
        {
          key: "question",
          label: "Question & answers",
          sortable: true,
          tdClass: "align-top quiz-bulk-question-td",
        },
        {
          key: "reviews",
          label: "Reviews",
          sortable: false,
          tdClass: "align-top quiz-bulk-reviews-td",
        },
        {
          key: "action",
          label: "Action",
          sortable: false,
          tdClass: "align-top text-nowrap quiz-bulk-action-td",
        },
      ],
    };
  },
  methods: {
    toggleBusy() {
      this.isBusy = !this.isBusy;
    },
    reviewVerdictLabel(isCorrect) {
      if (isCorrect === 1) return "Correct";
      if (isCorrect === 0) return "Incorrect";
      if (isCorrect === 2) return "Skipped";
      return "—";
    },
    removeRowFromTable(id) {
      const idx = this.tableData.findIndex((r) => r.id === id);
      if (idx === -1) return;
      this.tableData.splice(idx, 1);
      this.totalRows = this.tableData.length;
      const maxPage = Math.max(1, Math.ceil(this.totalRows / this.perPage) || 1);
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    },
    async patchQuizQuestionStatus(row, { is_approved, is_rejected }, actionLabel) {
      const id = row && row.id;
      if (!id || this.approvalLoading[id]) return;
      this.$set(this.approvalLoading, id, true);
      this.$set(this.approvalAction, id, actionLabel);
      const formData = new FormData();
      formData.append("csrf_token", this.csrf_token || "");
      formData.append("_method", "PATCH");
      if (is_approved !== undefined) {
        formData.append("is_approved", is_approved);
      }
      if (is_rejected !== undefined) {
        formData.append("is_rejected", is_rejected);
      }
      try {
        await Games.editQuizQuestion(id, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.removeRowFromTable(id);
        this.$bvToast?.toast("Question updated", {
          title: "Success",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        const err =
          error?.response?.data?.error != null
            ? Array.isArray(error.response.data.error)
              ? error.response.data.error.join(", ")
              : error.response.data.error
            : error?.message || "Failed to update question";
        this.$bvToast?.toast(err, {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.$set(this.approvalLoading, id, false);
        this.$delete(this.approvalAction, id);
      }
    },
    async approveRow(row) {
      await this.patchQuizQuestionStatus(
        row,
        { is_approved: 1, is_rejected: 0 },
        "approve"
      );
    },
    async rejectRow(row) {
      const ok = await Swal.fire({
        title: "Reject this question?",
        text: "It will be marked as rejected and hidden from players.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f46a6a",
        cancelButtonColor: "#74788d",
        confirmButtonText: "Yes, reject",
      });
      if (!ok.value) return;
      await this.patchQuizQuestionStatus(
        row,
        { is_approved: 0, is_rejected: 1 },
        "reject"
      );
    },
    async loadQueue() {
      this.toggleBusy();
      try {
        const params = new URLSearchParams({
          min_reviewers: String(this.minReviewers),
          pending_only: "1",
        });
        const res = await Games.getQuizBulkApprovalQueue(params.toString());
        this.tableData = Array.isArray(res.data.data) ? res.data.data : [];
        this.totalRows = this.tableData.length;
        this.currentPage = 1;
      } catch (error) {
        const err =
          error?.response?.data?.error != null
            ? Array.isArray(error.response.data.error)
              ? error.response.data.error.join(", ")
              : error.response.data.error
            : error?.message || "Failed to load queue";
        this.$bvToast?.toast(err, {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        this.tableData = [];
        this.totalRows = 0;
      } finally {
        this.toggleBusy();
      }
    },
  },
  mounted() {
    this.loadQueue();
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <p class="text-muted mb-3">
              Questions listed here have at least
              <strong>{{ minReviewers }}</strong>
              review row(s), and are still pending final approval.
            </p>

            <div class="d-flex flex-wrap align-items-end gap-3 mb-3">
              <div>
                <label class="form-label mb-1 small text-muted"
                  >Minimum reviews required</label
                >
                <select
                  v-model.number="minReviewers"
                  class="form-select form-select-sm"
                  style="width: 5rem"
                  @change="loadQueue"
                >
                  <option :value="3">3</option>
                  <option :value="4">4</option>
                  <option :value="5">5</option>
                </select>
              </div>
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="loadQueue"
              >
                <i class="mdi mdi-refresh me-1"></i> Refresh
              </button>
              <router-link class="btn btn-outline-secondary btn-sm" to="/monitor/quiz">
                <i class="mdi mdi-arrow-left me-1"></i> Back to Quiz list
              </router-link>
              <span v-if="tableData.length" class="text-muted small ms-auto">
                {{ tableData.length }} question(s) in queue
              </span>
            </div>

            <div class="table-responsive">
              <b-table
                :items="tableData"
                :busy="isBusy"
                :fields="fields"
                responsive="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                show-empty
                empty-text="No questions in this queue. Try lowering the minimum review count or check back later."
              >
                <template #cell(id)="data">
                  <div class="quiz-bulk-id-cell">
                    <div class="fw-bold text-dark fs-6">{{ data.item.id }}</div>
                    <div class="mt-1">
                      <span
                        v-if="data.item.difficulty"
                        class="badge bg-light text-dark border text-capitalize"
                      >{{ data.item.difficulty }}</span>
                      <span v-else class="text-muted small">—</span>
                    </div>
                  </div>
                </template>

                <template #cell(question)="data">
                  <div>
                    <router-link
                      class="quiz-bulk-q-link"
                      :to="`/games/quiz/question/${data.item.id}`"
                    >
                      {{ data.item.question }}
                    </router-link>
                    <ul
                      v-if="data.item.answers && data.item.answers.length"
                      class="quiz-bulk-answers list-unstyled mt-2 mb-0"
                    >
                      <li
                        v-for="(ans, i) in data.item.answers"
                        :key="ans.id || i"
                        class="quiz-bulk-answer-line small"
                        :class="{
                          'quiz-bulk-answer-correct': ans.correct_answer == 1,
                        }"
                      >
                        <span
                          v-if="ans.correct_answer == 1"
                          class="badge bg-success me-1 align-middle"
                          >Correct</span
                        >
                        <span class="text-break">{{ ans.answer }}</span>
                      </li>
                    </ul>
                    <p v-else class="text-muted small fst-italic mb-0 mt-2">
                      No answers loaded
                    </p>
                  </div>
                </template>

                <template #cell(reviews)="data">
                  <div class="quiz-bulk-reviews-wrap">
                    <table
                      v-if="data.item.reviews && data.item.reviews.length"
                      class="table table-sm table-bordered quiz-bulk-reviews-table mb-0"
                    >
                      <thead class="table-light">
                        <tr>
                          <th scope="col">Reviewer</th>
                          <th scope="col" class="text-center">Type</th>
                          <th scope="col" class="text-center">Verdict</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(rev, idx) in data.item.reviews"
                          :key="idx"
                        >
                          <td class="small text-break">
                            {{ rev.reviewer_name || "—" }}
                          </td>
                          <td class="text-center small">
                            <span
                              v-if="rev.reviewer_type"
                              class="badge"
                              :class="{
                                'bg-primary': rev.reviewer_type === 'human',
                                'bg-info': rev.reviewer_type === 'ai',
                                'bg-secondary':
                                  rev.reviewer_type !== 'human' &&
                                  rev.reviewer_type !== 'ai',
                              }"
                              >{{ rev.reviewer_type }}</span
                            >
                            <span v-else class="text-muted">—</span>
                          </td>
                          <td class="text-center small">
                            <span
                              class="badge"
                              :class="{
                                'bg-success': rev.is_correct === 1,
                                'bg-danger': rev.is_correct === 0,
                                'bg-secondary': rev.is_correct === 2,
                                'bg-warning text-dark':
                                  rev.is_correct !== 1 &&
                                  rev.is_correct !== 0 &&
                                  rev.is_correct !== 2,
                              }"
                            >
                              {{ reviewVerdictLabel(rev.is_correct) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <span v-else class="text-muted fst-italic small">No reviews</span>
                  </div>
                </template>

                <template #cell(action)="data">
                  <div class="d-flex flex-column gap-2 quiz-bulk-action-btns">
                    <button
                      type="button"
                      class="btn btn-success btn-sm d-inline-flex align-items-center justify-content-center"
                      :disabled="!!approvalLoading[data.item.id]"
                      @click="approveRow(data.item)"
                    >
                      <b-spinner
                        v-if="
                          approvalLoading[data.item.id] &&
                          approvalAction[data.item.id] === 'approve'
                        "
                        small
                        class="me-1"
                        role="status"
                      ></b-spinner>
                      <i
                        v-else-if="!approvalLoading[data.item.id]"
                        class="mdi mdi-check me-1"
                      ></i>
                      Approve
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-danger btn-sm d-inline-flex align-items-center justify-content-center"
                      :disabled="!!approvalLoading[data.item.id]"
                      @click="rejectRow(data.item)"
                    >
                      <b-spinner
                        v-if="
                          approvalLoading[data.item.id] &&
                          approvalAction[data.item.id] === 'reject'
                        "
                        small
                        class="me-1"
                        role="status"
                      ></b-spinner>
                      <i
                        v-else-if="!approvalLoading[data.item.id]"
                        class="mdi mdi-close me-1"
                      ></i>
                      Reject
                    </button>
                  </div>
                </template>

                <template #table-busy>
                  <div class="text-center text-primary my-3">
                    <b-spinner class="align-middle"></b-spinner>
                    <span class="ms-2">Loading…</span>
                  </div>
                </template>
              </b-table>
            </div>
            <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                    ></b-pagination>
                  </ul>
                </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
::v-deep td.quiz-bulk-id-td {
  width: 6rem;
  vertical-align: top;
}

::v-deep td.quiz-bulk-question-td {
  max-width: 36rem;
}

.quiz-bulk-q-link {
  font-weight: 600;
  color: #5b73e8;
  text-decoration: none;
  line-height: 1.45;
  word-break: break-word;
}

.quiz-bulk-q-link:hover {
  text-decoration: underline;
}

.quiz-bulk-answer-line {
  padding: 4px 0;
  border-bottom: 1px dashed #e9ecef;
  line-height: 1.4;
}

.quiz-bulk-answer-line:last-child {
  border-bottom: none;
}

.quiz-bulk-answer-correct {
  background: rgba(25, 135, 84, 0.06);
  padding-left: 6px !important;
  margin-left: -6px;
  border-radius: 4px;
}

::v-deep td.quiz-bulk-reviews-td {
  min-width: 18rem;
  max-width: 28rem;
  vertical-align: top;
}

::v-deep td.quiz-bulk-action-td {
  vertical-align: top;
}

.quiz-bulk-reviews-wrap {
  padding: 2px 0;
}

.quiz-bulk-reviews-table {
  font-size: 0.8125rem;
}

.quiz-bulk-reviews-table thead th {
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #495057;
  white-space: nowrap;
  padding: 0.35rem 0.5rem;
}

.quiz-bulk-reviews-table tbody td {
  padding: 0.4rem 0.5rem;
  vertical-align: middle;
}
</style>
